import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, PartyPopper } from "lucide-react";
import { api } from "@/lib/api";
import { formatEUR } from "@/lib/format";
import { useCart } from "@/context/CartContext";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [status, setStatus] = useState("checking"); // checking | paid | timeout | error
  const [amount, setAmount] = useState(null);
  const [orderNumber, setOrderNumber] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let polls = 0;
    let active = true;

    const poll = async () => {
      if (!active) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setAmount(data.amount);
          setOrderNumber(data.order_number);
          setStatus("paid");
          if (!cleared.current) {
            cleared.current = true;
            clearCart();
          }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus("error");
          return;
        }
      } catch (e) {
        // keep polling
      }
      polls += 1;
      if (polls >= MAX_POLLS) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="payment-success-page">
      {status === "checking" && (
        <>
          <Loader2 className="h-16 w-16 text-amber-500 mx-auto mb-6 animate-spin" />
          <h1 className="font-display text-3xl text-stone-100 mb-2">Confirmation du paiement…</h1>
          <p className="text-stone-400">Merci de patienter quelques instants.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <div className="relative inline-block mb-6">
            <CheckCircle2 className="h-20 w-20 text-green-500 mx-auto" />
            <PartyPopper className="h-8 w-8 text-amber-500 absolute -top-2 -right-4" />
          </div>
          <h1 className="font-display text-4xl font-black text-stone-50 mb-3">Commande confirmée !</h1>
          <p className="text-stone-300 mb-2">Merci pour votre confiance. Votre commande est en préparation.</p>
          {orderNumber && (
            <p className="text-stone-300 mb-2" data-testid="success-order-number">
              Numéro de commande : <span className="font-mono-tech text-amber-400 font-bold">{orderNumber}</span>
            </p>
          )}
          {amount != null && (
            <p className="font-mono-tech text-2xl text-amber-400 mb-8">Montant : {formatEUR(amount)}</p>
          )}
          <p className="text-sm text-stone-500 mb-8">
            Un email de confirmation vous a été envoyé. Conservez votre numéro pour{" "}
            <Link to={`/suivi-commande${orderNumber ? `?order=${orderNumber}` : ""}`} className="text-amber-400 hover:underline" data-testid="success-track-link">
              suivre votre commande
            </Link>
            .
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/boutique" className="btn-ember rounded-xl px-6 py-3">Continuer mes achats</Link>
            <Link to="/" className="px-6 py-3 rounded-xl border border-amber-900/40 text-stone-300 hover:border-amber-600">Accueil</Link>
          </div>
        </>
      )}
      {(status === "timeout" || status === "error") && (
        <>
          <XCircle className="h-20 w-20 text-red-500 mx-auto mb-6" />
          <h1 className="font-display text-3xl text-stone-100 mb-3">
            {status === "timeout" ? "Paiement en cours de traitement" : "Un problème est survenu"}
          </h1>
          <p className="text-stone-400 mb-8">
            {status === "timeout"
              ? "Votre paiement est peut-être encore en cours. Vérifiez votre email de confirmation."
              : "Le paiement n'a pas pu être confirmé. Aucun montant n'a été débité si l'opération a échoué."}
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/paiement" className="btn-ember rounded-xl px-6 py-3">Réessayer</Link>
            <Link to="/contact" className="px-6 py-3 rounded-xl border border-amber-900/40 text-stone-300 hover:border-amber-600">Nous contacter</Link>
          </div>
        </>
      )}
    </div>
  );
}
